import { __awaiter } from "tslib";
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
import { extractAreaPermission, extractAreaPermissions, getUserRoles } from '@/pages/login/utils';
import { convertS4RRoleToMerchandisingRoles, decodeJWT, getTenantName } from '@/utils';
import { getToken } from '@/auth/auth-access';
import { ls, USER_PROFILE_CACHE_KEY } from '@/stores/local-storage';
function isJwtFormattedToken(token) {
    return token.split('.').length === 3;
}
const IDENTITY = 'identity';
const IDENTITY_USER_PATH = `/${IDENTITY}/users`;
const USER_PATH = 'user';
const SETTINGS_PATH = 'settings';
function persistUserProfileCache(profile) {
    ls.setItem(USER_PROFILE_CACHE_KEY, profile);
}
/**
 * Transforms a raw UserProfile API response into the ActiveUser shape used
 * throughout the application. Derives role, areaPermissions, allowedAccess,
 * and tenantName from the server-supplied permissions list.
 */
export function transformUserProfileResponse(response) {
    const role = getUserRoles(response.permissions);
    const areaPermissions = extractAreaPermissions(response.permissions);
    const allowedAccess = convertS4RRoleToMerchandisingRoles(role);
    return Object.assign(Object.assign({}, response), { role: role, areaPermissions,
        allowedAccess, tenantName: getTenantName() || '', permissions: [...response.permissions] });
}
/**
 * After a successful getUserProfile fetch, reads the JWT token to extract any
 * implicit area permissions and patches them into the cached ActiveUser entry.
 *
 * - If `queryFulfilled` rejects (network error, 401, etc.), the function
 *   no-ops silently. The query error is still surfaced by RTK Query's
 *   own error handling — this callback simply skips the cache patch.
 * - If the token is absent, malformed, or carries no implicit roles, the
 *   function no-ops and the explicit permissions already in cache are preserved.
 */
export function applyImplicitPermissions(dispatch, queryFulfilled) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        try {
            yield queryFulfilled;
            const token = yield getToken();
            if (!isJwtFormattedToken(token))
                return;
            const decodedToken = decodeJWT(token);
            const implicitPermissions = (((_a = decodedToken === null || decodedToken === void 0 ? void 0 : decodedToken.implicitRoles) === null || _a === void 0 ? void 0 : _a.map((p) => extractAreaPermission(p)).filter(Boolean)) || []);
            if (implicitPermissions.length === 0)
                return;
            dispatch(userProfileApi.util.updateQueryData('getUserProfile', undefined, (draft) => {
                draft.areaPermissions = [...draft.areaPermissions, ...implicitPermissions];
            }));
        }
        catch (_b) {
            // token unavailable — implicit permissions skipped, explicit permissions remain in cache
        }
    });
}
/**
 * User Profile API - RTK Query endpoints for user identity and settings
 *
 * Uses injectEndpoints to extend the base ccApi with user-specific endpoints.
 */
export const userProfileApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Fetches the currently authenticated user's profile including settings,
         * and derives the ActiveUser shape (role, areaPermissions, allowedAccess, tenantName).
         *
         * @endpoint GET /identity/users/profile
         * @returns ActiveUser with derived fields
         */
        getUserProfile: builder.query({
            query: () => `${IDENTITY_USER_PATH}/profile`,
            providesTags: [CC_API_TAGS.UserProfile],
            transformResponse: transformUserProfileResponse,
            onQueryStarted: (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { dispatch, queryFulfilled, getCacheEntry }) {
                try {
                    yield queryFulfilled;
                }
                catch (_b) {
                    return; // Query failed — do not persist stale cache data
                }
                yield applyImplicitPermissions(dispatch, queryFulfilled);
                // Read the fully-patched ActiveUser from the cache (includes implicit permissions)
                const result = getCacheEntry();
                if (result.data) {
                    persistUserProfileCache(result.data);
                }
            }),
        }),
        /**
         * Updates the settings for the currently authenticated user.
         * After a successful update, automatically invalidates the UserProfile cache
         * to trigger a refetch with the updated settings.
         *
         * @endpoint PUT /user/settings
         * @param settings - Partial settings object with fields to update
         * @returns Updated settings
         */
        updateUserSettings: builder.mutation({
            query: (settings) => ({
                url: `/${USER_PATH}/${SETTINGS_PATH}`,
                method: 'PUT',
                body: settings,
            }),
            invalidatesTags: [CC_API_TAGS.UserProfile],
        }),
    }),
});
export const { useGetUserProfileQuery, useUpdateUserSettingsMutation, } = userProfileApi;
